// Primitivas del SDK (ADR 0004 §1–§4.2) sin dependencias: WebCrypto para SHA-256/HMAC,
// enteros big-endian, hex, JCS mínima (RFC 8785 para objetos, arrays, cadenas, enteros y
// decimales) y dHash 9×8 en gris.

const encoder = new TextEncoder();
const HEX_RE = /^(?:[0-9a-f]{2})*$/;

function subtle(): SubtleCrypto {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('webcrypto_unavailable');
  return s;
}

/** Bytes UTF-8 de una cadena. */
export function utf8(s: string): Uint8Array {
  return encoder.encode(s);
}

/** Hex minúsculas de unos bytes. */
export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/** Bytes de un hex (minúsculas o mayúsculas, longitud par). */
export function fromHex(hex: string): Uint8Array {
  const h = hex.toLowerCase();
  if (!HEX_RE.test(h)) throw new TypeError('invalid_hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Concatena bloques de bytes. */
export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Entero sin signo de 32 bits, big-endian. */
export function u32(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xffff_ffff) throw new RangeError('u32_out_of_range');
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, n, false);
  return out;
}

/** Entero sin signo de 64 bits, big-endian (n ≤ 2^53−1). */
export function u64(n: number): Uint8Array {
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError('u64_out_of_range');
  const out = new Uint8Array(8);
  const view = new DataView(out.buffer);
  view.setUint32(0, Math.floor(n / 2 ** 32), false);
  view.setUint32(4, n >>> 0, false);
  return out;
}

/** Copia a un `ArrayBuffer` propio (WebCrypto no acepta vistas sobre `SharedArrayBuffer`). */
function own(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return new Uint8Array(bytes);
}

/** SHA-256 de unos bytes (32 bytes). */
export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', own(data)));
}

/** SHA-256 en hex de bytes o de una cadena UTF-8. */
export async function sha256Hex(data: Uint8Array | string): Promise<string> {
  return toHex(await sha256(typeof data === 'string' ? utf8(data) : data));
}

/** HMAC-SHA-256(key, msg). */
export async function hmacSha256(key: Uint8Array, msg: Uint8Array): Promise<Uint8Array> {
  const k = await subtle().importKey('raw', own(key), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  return new Uint8Array(await subtle().sign('HMAC', k, own(msg)));
}

/** `n` bytes aleatorios en hex. */
export function randomHex(n: number): string {
  return toHex(globalThis.crypto.getRandomValues(new Uint8Array(n)));
}

/**
 * JSON canónico (RFC 8785) para los valores que firma la captura: objetos planos (claves
 * ordenadas por unidades UTF-16, `undefined` omitido), arrays, cadenas, booleanos, null y
 * números finitos con la serialización de ECMAScript. Igual que `canonicalize` de `@kyp/core`.
 */
export function canonicalize(value: unknown): string {
  if (value === null) return 'null';
  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      if (!Number.isFinite(value)) throw new TypeError('jcs_non_finite_number');
      return JSON.stringify(value);
    case 'string':
      return JSON.stringify(value);
    case 'object': {
      if (Array.isArray(value))
        return '[' + value.map((v) => (v === undefined ? 'null' : canonicalize(v))).join(',') + ']';
      const proto: unknown = Object.getPrototypeOf(value);
      if (proto !== Object.prototype && proto !== null)
        throw new TypeError('jcs_unsupported_object');
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .sort();
      return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalize(obj[k])).join(',') + '}';
    }
    default:
      throw new TypeError('jcs_unsupported_type');
  }
}

/** `utf8(ctx) ‖ 0x00 ‖ JCS(obj)`: lo único que se firma (ADR 0004 §2). */
export function contextMessage(ctx: string, obj: unknown): Uint8Array {
  return concatBytes(utf8(ctx), new Uint8Array([0]), utf8(canonicalize(obj)));
}

/** Gris BT.601 de un píxel. */
export function grayOf(r: number, g: number, b: number): number {
  return Math.round(0.299 * r + 0.587 * g + 0.114 * b);
}

/**
 * Reduce una imagen RGBA a `w×h` en gris por media de área (como `resizeGray` de core):
 * primero el gris de cada píxel y luego la media de la caja.
 */
export function grayResize(
  rgba: ArrayLike<number>,
  width: number,
  height: number,
  w: number,
  h: number,
): Uint8Array {
  if (rgba.length < width * height * 4) throw new RangeError('rgba_too_short');
  const out = new Uint8Array(w * h);
  const sx = width / w;
  const sy = height / h;
  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * sy);
    const y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * sx);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      let sum = 0;
      let n = 0;
      for (let yy = y0; yy < y1 && yy < height; yy++)
        for (let xx = x0; xx < x1 && xx < width; xx++) {
          const p = (yy * width + xx) * 4;
          sum += grayOf(rgba[p]!, rgba[p + 1]!, rgba[p + 2]!);
          n++;
        }
      out[y * w + x] = n === 0 ? 0 : Math.round(sum / n);
    }
  }
  return out;
}

/** dHash de 72 grises (9×8): bit = gray[f][c] > gray[f][c+1], fila a fila, MSB primero, 16 hex. */
export function dhashFromGray(gray: ArrayLike<number>): string {
  if (gray.length !== 72) throw new RangeError('dhash_needs_9x8_gray');
  let hex = '';
  for (let row = 0; row < 8; row++) {
    let byte = 0;
    for (let col = 0; col < 8; col++)
      byte = (byte << 1) | (gray[row * 9 + col]! > gray[row * 9 + col + 1]! ? 1 : 0);
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

/** dHash desde un `ImageData` (o cualquier RGBA con ancho y alto). */
export function dhashFromImageData(img: {
  data: ArrayLike<number>;
  width: number;
  height: number;
}): string {
  return dhashFromGray(grayResize(img.data, img.width, img.height, 9, 8));
}

/** Distancia de Hamming entre dos huellas hex de la misma longitud. */
export function hamming(a: string, b: string): number {
  if (a.length !== b.length) throw new TypeError('hamming_length_mismatch');
  let d = 0;
  for (let i = 0; i < a.length; i++) {
    let x = Number.parseInt(a[i]!, 16) ^ Number.parseInt(b[i]!, 16);
    while (x) {
      d += x & 1;
      x >>= 1;
    }
  }
  return d;
}

/** Estadísticos de un gris: media y desviación típica. */
export function grayStats(gray: ArrayLike<number>): { mean: number; sd: number } {
  let s = 0;
  for (let i = 0; i < gray.length; i++) s += gray[i]!;
  const mean = gray.length ? s / gray.length : 0;
  let v = 0;
  for (let i = 0; i < gray.length; i++) v += (gray[i]! - mean) ** 2;
  return { mean, sd: gray.length ? Math.sqrt(v / gray.length) : 0 };
}

/** Media de |a−b| entre dos grises del mismo tamaño. */
export function meanAbsDiff(a: ArrayLike<number>, b: ArrayLike<number>): number {
  if (a.length !== b.length || a.length === 0) throw new RangeError('gray_size_mismatch');
  let s = 0;
  for (let i = 0; i < a.length; i++) s += Math.abs(a[i]! - b[i]!);
  return s / a.length;
}
